import { useLocaleContext } from 'providers/LocaleProvider';
import React, { useState, useCallback, createContext, useContext } from 'react';
import { bool, node, object } from 'prop-types';
import styled from 'styled-components';
import { fromJS } from 'immutable';
import camelCase from 'lodash/camelCase';
import TextField, { TextArea } from '@seaweb/coral/components/TextField';
import Field from '@seaweb/coral/components/Field';
import Radio, { RadioGroup } from '@seaweb/coral/components/Radio';
import merge from 'lodash/merge';
import { createErrorMessage, formatErrorMessage } from 'utils/displays';
import { ErrorCodes } from 'consts/enums';
import { FetchError } from 'utils/errors';
import Unselected from 'images/Unselected.svg';
import Selected from 'images/Selected.svg';
import NavSearchField from './SearchField';

const ContactFormContext = createContext(null);
export const useContactFormContext = () => useContext(ContactFormContext);

const CONTACT_US_TYPE = {
  QUESTION: 1,
  ISSUE: 2,
};

export const ContactFormDefaultData = {
  name: '',
  email: '',
  description: '',
  type: '',
};

export default function ContactFormProvider({ initialData, children }) {
  // don't use merge, it may hide an error
  const [immData, setImmData] = useState(() =>
    fromJS(initialData || ContactFormDefaultData)
  );
  const handleChange = useCallback(
    (keyPath, value) => setImmData((cur) => cur.setIn(keyPath, fromJS(value))),
    []
  );

  const data = immData.toJS();

  const [fieldErrors, setFieldErrors] = useState({});

  const handleErrors = useCallback((err) => {
    const iterError = (path, message) => {
      if (path.length === 1) {
        return { [path[0]]: message };
      }
      return {
        [path[0]]: iterError(path.slice(1), message),
      };
    };
    if (err instanceof FetchError && err.code === ErrorCodes.BadFieldRequest) {
      setFieldErrors(
        iterError(err.data.path.map(camelCase), createErrorMessage(err.data))
      );
    } else if (
      err instanceof FetchError &&
      err.code === ErrorCodes.MultipleBadFieldRequest
    ) {
      const newFieldErrors = {};
      err.data.forEach((badFieldError) => {
        merge(
          newFieldErrors,
          iterError(
            badFieldError.path.map(camelCase),
            createErrorMessage(badFieldError)
          )
        );
      });
      setFieldErrors(newFieldErrors);
    }
  }, []);

  const resetError = useCallback(() => {
    setFieldErrors({});
  }, []);

  return (
    <ContactFormContext.Provider
      value={{
        data,
        onChange: handleChange,
        cleanedData: {
          ...data,
        },
        fieldErrors,
        onError: handleErrors,
        resetError,
      }}
    >
      {children}
    </ContactFormContext.Provider>
  );
}

ContactFormProvider.propTypes = {
  initialData: object,
  children: node,
};

const FormField = styled(Field)`
  width: 100%;
  max-width: 350px;
  flex-wrap: wrap;

  > * {
    width: 100%;
  }

  @media screen and (max-width: 760px) {
    max-width: unset;
  }
`;

const FirstFormField = styled(FormField)`
  margin-right: 20px;

  @media screen and (max-width: 760px) {
    margin-right: 0;
    margin-bottom: 0;
  }
`;

const TypeRadio = styled(Radio)`
  width: 250px;

  @media screen and (max-width: 760px) {
    width: initial;
  }
`;

const DescriptionField = styled(Field)`
  width: 100%;
  max-width: 720px;
  > * {
    width: 100%;
  }
`;

const Form = styled.div`
  width: 100%;
  margin: 0 auto;
  max-width: 750px;
`;

const DescriptionSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  margin: 26px 0;
`;

const ContactInfoSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  margin: 40px auto;
`;

const SectionTitle = styled.div`
  width: 100%;
  color: #383838;
  font-size: 16px;
  font-weight: 600;
`;

const StyledTitle = styled.h2`
  font-size: 32px;
  font-weight: bold;
  width: 100%;
  margin: 24px auto;
  text-align: center;
`;

const StyledDescription = styled.div`
  opacity: 0.7;
  font-size: 14px;
  width: 100%;
  margin: 0px auto 20px;
  text-align: center;
  line-height: 26px;
`;

const StyledNeedSupport = styled.div`
  opacity: 0.7;
  font-size: 14px;
  width: 100%;
  margin: 80px auto 20px;
  text-align: center;
`;

export function ContactForm() {
  const { data, onChange, fieldErrors } = useContactFormContext();
  const { locale: t } = useLocaleContext();

  const renderReportIssueHelp = () => {
    let reportHelpUrl = 'https://help.seatalk.io/categories/22/article/552';
    if (window.location.hostname.endsWith('.tw')) {
      reportHelpUrl = 'https://help.seatalk.tw/categories/104/article/609';
    } else if (window.location.hostname.endsWith('.in.th')) {
      reportHelpUrl = 'https://help.seatalk.in.th/categories/79/article/638';
    } else if (window.location.hostname.endsWith('.vn')) {
      reportHelpUrl = 'https://help.seatalk.vn/categories/60/article/563';
    } else if (window.location.hostname.endsWith('.vn')) {
      reportHelpUrl = 'https://help.seatalk.co.id/categories/131/article/557';
    }
    const moreTipsString = t('hc.contact.report_issue_more_tips');
    const moreTipsStringList = moreTipsString.split('{{linkHere}}');
    return (
      <div
        style={{
          border: '1px solid #1B92F5',
          padding: 16,
          background: '#F9FBFE',
          borderRadius: '4px',
        }}
      >
        {moreTipsStringList[0]}
        &nbsp;
        <a href={reportHelpUrl} style={{ color: '#1B92F5' }}>
          {t('hc.contact.report_issue_more_tips_here')}
        </a>
        {moreTipsStringList[1]}
      </div>
    );
  };

  const RenderHelpTypeRadio = ({ isSelected = false }) => {
    if (isSelected) {
      return <Selected style={{ marginRight: '8px' }} />;
    }
    return <Unselected style={{ marginRight: '8px' }} />;
  };

  RenderHelpTypeRadio.propTypes = {
    isSelected: bool,
  };

  return (
    <Form>
      <StyledTitle>{t('hc.contact.contact_us')}</StyledTitle>
      <StyledDescription>{t('hc.contact.description')}</StyledDescription>
      <NavSearchField
        isWhiteHeader
        style={{
          borderRadius: '4px',
          width: '100%',
          height: '48px',
          color: '#202325',
          background: '#fff !important',
        }}
      />
      <StyledNeedSupport>{t('hc.contact.need_support')}</StyledNeedSupport>
      <DescriptionSection>
        <SectionTitle>{t('hc.contact.what_need_help')}</SectionTitle>
        <DescriptionField
          label=""
          invalid={!!fieldErrors.type}
          message={fieldErrors.type}
        >
          <RadioGroup
            onChange={(value) => onChange(['type'], value)}
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <TypeRadio
              value={CONTACT_US_TYPE.QUESTION}
              icon={<RenderHelpTypeRadio />}
            >
              {t('hc.contact.ask_question')}
            </TypeRadio>
            <TypeRadio
              value={CONTACT_US_TYPE.ISSUE}
              icon={<RenderHelpTypeRadio />}
            >
              {t('hc.contact.report_issue')}
            </TypeRadio>
          </RadioGroup>
        </DescriptionField>
      </DescriptionSection>
      {data.type === CONTACT_US_TYPE.ISSUE && renderReportIssueHelp()}
      {data.type && (
        <>
          <ContactInfoSection>
            <SectionTitle>{t('hc.contact.contact_info')}</SectionTitle>
            <FirstFormField
              label=""
              invalid={!!fieldErrors.name}
              message={fieldErrors.name}
            >
              <TextField
                fullWidth
                name="fullName"
                placeholder={t('hc.contact.full_name')}
                value={data.name}
                style={{ backgroundColor: 'white', height: '40px' }}
                onChange={(e) => onChange(['name'], e.target.value)}
              />
            </FirstFormField>
            <FormField
              label=""
              invalid={!!fieldErrors.email}
              message={formatErrorMessage(fieldErrors.email, {
                fieldName: 'email',
              })}
            >
              <TextField
                fullWidth
                name="email"
                placeholder={t('hc.contact.email')}
                value={data.email}
                style={{ backgroundColor: 'white', height: '40px' }}
                onChange={(e) => onChange(['email'], e.target.value)}
              />
            </FormField>
          </ContactInfoSection>
          <DescriptionSection>
            <SectionTitle>{t('hc.contact.question_description')}</SectionTitle>
            <DescriptionField
              label=""
              invalid={!!fieldErrors.description}
              message={fieldErrors.description}
            >
              <TextArea
                fullWidth
                name="description"
                placeholder={
                  data.type === CONTACT_US_TYPE.ISSUE
                    ? t('hc.contact.describe_issue')
                    : t('hc.contact.describe_question')
                }
                minRows={5}
                value={data.description}
                style={{ backgroundColor: 'white', height: '120px' }}
                onChange={(e) => onChange(['description'], e.target.value)}
              />
            </DescriptionField>
          </DescriptionSection>
        </>
      )}
    </Form>
  );
}
